// ============================================================================
// Templates page — pure helpers (no React, no side effects).
// ----------------------------------------------------------------------------
// Local utilities for the Programs (workout templates) screen:
//   • locale-aware exercise name resolution (name_en / name_zh, graceful)
//   • the editor's draft-item shape + conversions to the TemplateInput payload
//   • immutable reorder / move helpers for the item list
//   • small formatting (created date, totals) — all "optional-first": missing
//     inputs yield null / empty, never a throw and never a fake zero.
// ============================================================================
import type { Exercise, Template, TemplateInput, TemplateItem } from "../../api";
import { intlLocale } from "../../i18n/config";

// --- locale-aware exercise naming ------------------------------------------

/** Pick the exercise name for the active locale, falling back across both. */
export function exerciseName(
  ex: Pick<Exercise, "name_en" | "name_zh"> | undefined,
  locale: string | undefined,
): string {
  if (!ex) return "";
  const zhFirst = (locale ?? "").toLowerCase().startsWith("zh");
  const en = ex.name_en?.trim() || "";
  const zh = ex.name_zh?.trim() || "";
  if (zhFirst) return zh || en;
  return en || zh;
}

/** A short, secondary descriptor (muscles / equipment) for catalog rows. */
export function exerciseMeta(
  ex: Pick<Exercise, "primary_muscles" | "equipment"> | undefined,
): string {
  if (!ex) return "";
  const bits = [ex.primary_muscles, ex.equipment]
    .map((s) => s?.trim())
    .filter((s): s is string => Boolean(s));
  return bits.join(" · ");
}

// --- editor draft items -----------------------------------------------------

/**
 * A draft template item the editor edits in local state. Numeric defaults are
 * kept as STRINGS so the inputs can be blank (optional-first); they convert to
 * numbers (or are dropped) only on save. `key` is a stable client-side id used
 * for React keys + reorder animations, since new items have no server id yet.
 */
export interface DraftItem {
  key: string;
  exerciseId: number;
  name: string;
  meta: string;
  sets: string;
  reps: string;
  weight: string;
}

let keySeq = 0;
/** A stable, monotonic client-side key for a freshly added draft item. */
export function nextKey(): string {
  keySeq += 1;
  return `it-${Date.now().toString(36)}-${keySeq}`;
}

const numToStr = (n: number | null | undefined): string =>
  n == null ? "" : String(n);

/** Build an editor draft from an exercise just picked from the catalog. */
export function draftFromExercise(ex: Exercise, locale: string | undefined): DraftItem {
  return {
    key: nextKey(),
    exerciseId: ex.id,
    name: exerciseName(ex, locale),
    meta: exerciseMeta(ex),
    sets: "3",
    reps: "10",
    weight: "",
  };
}

/**
 * Hydrate editor drafts from a saved template, resolving each item's exercise
 * name/meta from the catalog when available (falls back to a placeholder). The
 * list is ordered by the stored `ord`, then by index, so it round-trips.
 */
export function draftsFromTemplate(
  template: Template,
  catalog: Map<number, Exercise>,
  locale: string | undefined,
): DraftItem[] {
  const items = [...(template.items ?? [])].sort(
    (a, b) => (a.ord ?? 0) - (b.ord ?? 0),
  );
  return items.map((it) => {
    const ex = it.exercise_id != null ? catalog.get(it.exercise_id) : undefined;
    return {
      key: nextKey(),
      exerciseId: it.exercise_id ?? -1,
      name: exerciseName(ex, locale),
      meta: exerciseMeta(ex),
      sets: numToStr(it.sets),
      reps: numToStr(it.reps),
      weight: numToStr(it.weight_kg),
    };
  });
}

// --- payload construction ---------------------------------------------------

const numOrNull = (s: string): number | null => {
  const v = s.trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

/** Convert the editor state into the API TemplateInput (optional-first). */
export function toTemplateInput(name: string, drafts: DraftItem[]): TemplateInput {
  const items: Array<Partial<TemplateItem>> = drafts.map((d, i) => ({
    exercise_id: d.exerciseId,
    ord: i,
    sets: numOrNull(d.sets),
    reps: numOrNull(d.reps),
    weight_kg: numOrNull(d.weight),
  }));
  return { name: name.trim(), items };
}

// --- immutable reorder ------------------------------------------------------

/** Move an item one slot up/down; returns a new array (no-op at the edges). */
export function moveItem<T>(arr: T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (target < 0 || target >= arr.length) return arr;
  const next = arr.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

// --- summaries / formatting -------------------------------------------------

/** Total prescribed sets across a template's items (null if none counted). */
export function totalSets(template: Template): number | null {
  let sum = 0;
  let any = false;
  for (const it of template.items ?? []) {
    if (it.sets != null) {
      sum += it.sets;
      any = true;
    }
  }
  return any ? sum : null;
}

/** A short, locale-aware created-date label, e.g. "16 Jun 2026". null-safe. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Compact "3×10" / "3 sets" prescription label for an item. Returns "" when
 * neither sets nor reps were set (optional-first — no fake zeros).
 */
export function prescription(
  sets: number | null,
  reps: number | null,
  setsWord: string,
): string {
  if (sets != null && reps != null) return `${sets}×${reps}`;
  if (sets != null) return `${sets} ${setsWord}`;
  if (reps != null) return `×${reps}`;
  return "";
}
